export const RUNTIME_MODES = {
  AUTO: "auto",
  APP: "app",
  DISPATCHER: "dispatcher",
};

export const APP_MODE_STORAGE_KEYS = {
  runtimeMode: "webdispatcher.runtimeMode",
  appMode: "webdispatcher.appMode",
};

const APP_MODE_DOMAIN_FALLBACKS = ["app.smartzos.cz"];
const DISPATCHER_MODE_DOMAIN_FALLBACKS = [
  "disp.smartzos.cz",
  "disptest.smartzos.cz",
];

const normalizeMode = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["app", "appmode", "mobile", "smallapp"].includes(normalized)) {
    return RUNTIME_MODES.APP;
  }
  if (["disp", "dispatcher", "webdispatcher", "wd"].includes(normalized)) {
    return RUNTIME_MODES.DISPATCHER;
  }
  if (["", "auto", "domain"].includes(normalized)) return RUNTIME_MODES.AUTO;
  return null;
};

const normalizeBoolean = (value) => {
  if (value === true || value === false) return value;
  const normalized = String(value || "").trim().toLowerCase();
  if (["true", "1", "yes", "y", "app"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "dispatcher", "disp"].includes(normalized)) {
    return false;
  }
  return null;
};

const getConfigValue = (key) => {
  if (typeof window !== "undefined" && window.appConfig?.[key] !== undefined) {
    return window.appConfig[key];
  }

  if (typeof process !== "undefined" && process.env?.[key] !== undefined) {
    return process.env[key];
  }

  return null;
};

const getStorageValue = (key) => {
  if (typeof window === "undefined") return null;

  try {
    return (
      window.sessionStorage?.getItem(key) ??
      window.localStorage?.getItem(key) ??
      null
    );
  } catch (_error) {
    return null;
  }
};

const normalizeDomainList = (value, fallback = []) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/[,\s]+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }

  return fallback;
};

const matchesHost = (hostname, patterns = []) => {
  const normalizedHost = String(hostname || "").trim().toLowerCase();
  if (!normalizedHost) return false;

  return patterns.some((pattern) => {
    const normalizedPattern = String(pattern || "").trim().toLowerCase();
    if (!normalizedPattern) return false;
    if (normalizedPattern.startsWith("*.")) {
      const suffix = normalizedPattern.slice(1);
      return normalizedHost.endsWith(suffix);
    }
    return normalizedHost === normalizedPattern;
  });
};

const getQueryRuntimeMode = (search = null) => {
  if (typeof window === "undefined" && search === null) return null;

  const params = new URLSearchParams(
    search === null ? window.location.search : search,
  );
  const explicitMode =
    params.get("runtimeMode") ||
    params.get("wdMode") ||
    params.get("mode");
  const mode = normalizeMode(explicitMode);
  if (mode && mode !== RUNTIME_MODES.AUTO) return mode;

  const explicitAppMode = normalizeBoolean(params.get("appMode"));
  if (explicitAppMode === true) return RUNTIME_MODES.APP;
  if (explicitAppMode === false) return RUNTIME_MODES.DISPATCHER;

  return null;
};

export const getRuntimeModeInfo = ({ search = null, hostname = null } = {}) => {
  const queryMode = getQueryRuntimeMode(search);
  if (queryMode) {
    return { mode: queryMode, source: "query" };
  }

  const configuredMode = normalizeMode(getConfigValue("REACT_APP_RUNTIME_MODE"));
  if (configuredMode && configuredMode !== RUNTIME_MODES.AUTO) {
    return { mode: configuredMode, source: "REACT_APP_RUNTIME_MODE" };
  }

  const configuredAppMode = normalizeBoolean(getConfigValue("REACT_APP_APP_MODE"));
  if (configuredAppMode === true) {
    return { mode: RUNTIME_MODES.APP, source: "REACT_APP_APP_MODE" };
  }
  if (configuredAppMode === false) {
    return { mode: RUNTIME_MODES.DISPATCHER, source: "REACT_APP_APP_MODE" };
  }

  const storedMode = normalizeMode(
    getStorageValue(APP_MODE_STORAGE_KEYS.runtimeMode),
  );
  if (storedMode && storedMode !== RUNTIME_MODES.AUTO) {
    return { mode: storedMode, source: APP_MODE_STORAGE_KEYS.runtimeMode };
  }

  const storedAppMode = normalizeBoolean(
    getStorageValue(APP_MODE_STORAGE_KEYS.appMode),
  );
  if (storedAppMode === true) {
    return { mode: RUNTIME_MODES.APP, source: APP_MODE_STORAGE_KEYS.appMode };
  }
  if (storedAppMode === false) {
    return {
      mode: RUNTIME_MODES.DISPATCHER,
      source: APP_MODE_STORAGE_KEYS.appMode,
    };
  }

  const resolvedHostname =
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  const appDomains = normalizeDomainList(
    getConfigValue("REACT_APP_APP_MODE_DOMAINS"),
    APP_MODE_DOMAIN_FALLBACKS,
  );
  const dispatcherDomains = normalizeDomainList(
    getConfigValue("REACT_APP_DISPATCHER_MODE_DOMAINS"),
    DISPATCHER_MODE_DOMAIN_FALLBACKS,
  );

  if (matchesHost(resolvedHostname, appDomains)) {
    return { mode: RUNTIME_MODES.APP, source: "domain" };
  }

  if (matchesHost(resolvedHostname, dispatcherDomains)) {
    return { mode: RUNTIME_MODES.DISPATCHER, source: "domain" };
  }

  return { mode: RUNTIME_MODES.DISPATCHER, source: "default" };
};

export const getRuntimeMode = () => getRuntimeModeInfo().mode;

export const isAppRuntimeMode = () => getRuntimeMode() === RUNTIME_MODES.APP;

export const isLegacyEventDetailValue = (value) =>
  /^\d+$/.test(String(value || "").trim());

export const getAppModeEventId = (search = null) => {
  if (typeof window === "undefined" && search === null) return null;

  const params = new URLSearchParams(
    search === null ? window.location.search : search,
  );
  const legacyDetailEventParam =
    !params.has("event") &&
    !params.has("eventId") &&
    !params.has("unit") &&
    !params.has("unitId") &&
    !params.has("unitID") &&
    !params.has("base") &&
    !params.has("baseId") &&
    !params.has("baseID") &&
    isLegacyEventDetailValue(params.get("detail"))
      ? params.get("detail")
      : null;

  return params.get("event") || params.get("eventId") || legacyDetailEventParam;
};
